// @ts-check
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { CsvDataTable } from 'core';
import { channelExportHistoryTableConfig } from 'core/components/Tables/configs';
import { getChannelExportHistoryDownloadUrl } from 'core/services/campaign-file-service';
import { setPageLoading } from 'core/store/slices/sharedSlice';

const HeaderRight = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  grid-gap: 2rem;
  align-items: end;

  .item {
    text-align: left;
  }

  .item:last-child {
    text-align: left;
  }

  .close-cell {
    justify-self: end;
    align-self: start;
  }

  .close-cell .btn-close-x {
    margin-left: 0;
  }
`;

// Interpret server-provided timestamps as UTC naive, then always display in Phoenix (America/Phoenix).
// Backend pattern: naive 'YYYY-MM-DD HH:MM:SS' represents a UTC instant (e.g. submitted_at 23:09:29, S3 key shows 160929 local Phoenix => +7h relationship).
// Steps: (1) normalize to ISO with 'Z'; (2) build Date; (3) format with timeZone: 'America/Phoenix'.
const parseUtcNaive = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === 'string') {
    // If the backend returns naive 'YYYY-MM-DD HH:MM:SS' (no 'T'), treat it as UTC.
    // If it already contains 'T' assume it's ISO and let native parsing handle it.
    if (!raw.includes('T')) {
      const isoLike = raw.trim().replace(' ', 'T') + 'Z';
      const d = new Date(isoLike);
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  // Fallback attempt.
  try {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

// Formatter: always show Phoenix time to all users.
const formatDateLong = (raw) => {
  if (!raw) return '';
  const d = parseUtcNaive(raw);
  if (!d || Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Phoenix',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
};

// Extract ASURITE portion of the email string.
const toAsurite = (val) => {
  if (!val || typeof val !== 'string') return '';
  const at = val.indexOf('@');
  return at === -1 ? val : val.slice(0, at);
};

/**
 * @param {{
 *  campaign: CampaignModel,
 *  onClose: () => void
 * }} props
 */
const ChannelExportHistoryModal = ({ campaign, onClose }) => {
  const dispatch = useDispatch();
  const historyList = useMemo(() => {
    const list = campaign?.channel_export_history;
    return Array.isArray(list) ? list : [];
  }, [campaign]);

  const columns = useMemo(
    () => channelExportHistoryTableConfig.columns.map((c) => c.id),
    [],
  );

  const records = useMemo(() => {
    if (historyList.length === 0) return [];
    const byDateDesc = [...historyList].sort((a, b) => {
      const tb = parseUtcNaive(b.submitted_at)?.getTime() ?? 0;
      const ta = parseUtcNaive(a.submitted_at)?.getTime() ?? 0;
      return tb - ta;
    });
    return byDateDesc.map((evt) => [
      formatDateLong(evt.submitted_at) ?? '',
      evt.activity ?? '',
      // Placeholder; actual link rendered via cellRender.
      evt.s3_location ?? '',
      toAsurite(evt.submitted_by) ?? '',
      evt.id ?? '',
    ]);
  }, [historyList]);

  const headerRightArea = (
    <HeaderRight>
      <div className="item">
        <div>
          <strong>Campaign Name</strong>
        </div>
        <div>{campaign?.campaignName}</div>
      </div>
      <div className="item">
        <div>
          <strong>Campaign ID</strong>
        </div>
        <div>{campaign?.campaignId}</div>
      </div>
      <div className="item close-cell">
        <button
          type="button"
          className="btn-close-x"
          aria-label="Close channel export history"
          data-ga-event="link"
          data-ga-action="click"
          data-ga-name="onclick"
          data-ga-type="cta"
          data-ga-region="main content"
          data-ga-section="Channel Export History Modal"
          data-ga-component=""
          data-ga-text="Close channel export history"
          onClick={onClose}
        >
          <span className="modal-close-icon" aria-hidden="true">
            <i className="fa-solid fa-xmark modal-close-icon__x" />
          </span>
        </button>
      </div>
    </HeaderRight>
  );

  // The Salesforc feature flag gate (SFMC) for rendering Channel export history is
  // handled in the parent DetailsList control.
  return (
    <div>
      <CsvDataTable
        loading={false}
        config={channelExportHistoryTableConfig}
        columns={columns}
        records={records}
        totalRecordCount={records.length}
        // Cap at 9 visible rows so short viewports still show the scrollbar controls.
        maxVisibleRows={9}
        headerRightArea={headerRightArea}
        caption="Channel export history"
        showNoDataPlaceholder
        noDataPlaceholder="No channel export history found"
        plainHeaders
        fixedLayout={false}
        wrapCells
        cellRender={({ row, colId, colIndex }) => {
          if (colId === 'campaign_export') {
            const s3 = row[2];
            const id = row[4];
            const dateText = row[0];
            return (
              <button
                type="button"
                className="btn btn-link p-0"
                aria-label={`Download file for ${dateText}`}
                data-ga-event="link"
                data-ga-action="download"
                data-ga-name="onclick"
                data-ga-type="cta"
                data-ga-region="main content"
                data-ga-section="Channel Export History Modal"
                data-ga-component=""
                data-ga-text={`Download file for ${dateText}`}
                onClick={async () => {
                  // Avoid fetch()/blob due to CORS on raw S3 bucket URLs.
                  // Obtain (or already have) presigned URL and trigger download via hidden iframe.
                  // Can't reliably enforce a custom filename client-side for cross-origin download.
                  // Filename will come from the S3 object key / Content-Disposition if present.
                  const isUrl = /^https?:\/\//i.test(s3);
                  try {
                    dispatch(setPageLoading(true));
                    let downloadUrl = s3;
                    if (!isUrl) {
                      const res = await getChannelExportHistoryDownloadUrl(id);
                      if (res?.url) downloadUrl = res.url;
                    }
                    if (downloadUrl) {
                      const iframe = document.createElement('iframe');
                      iframe.style.display = 'none';
                      iframe.src = downloadUrl;
                      document.body.appendChild(iframe);
                      // Best-effort cleanup.
                      const cleanup = () =>
                        setTimeout(() => iframe.remove(), 15000);
                      iframe.onload = cleanup;
                      cleanup();
                    }
                  } catch {
                    /* Intentionally ignored: non-blocking download failure (network/presign issue).
                       We can add user-facing notification or telemetry if failure rates increase. */
                  } finally {
                    dispatch(setPageLoading(false));
                  }
                }}
              >
                Download file
              </button>
            );
          }
          return row[colIndex];
        }}
      />
    </div>
  );
};

export { ChannelExportHistoryModal };
